import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, extractError, API_BASE, TOKEN_KEY } from "../../lib/api";
import { formatMoney } from "../../lib/money";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Alert, AlertDescription } from "../../components/ui/alert";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Download, FileBarChart, TrendingUp, Users, FolderKanban, Tags, CreditCard, MessageSquare, Activity, Truck, Building2 } from "lucide-react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";

const REPORTS = [
  { key: "reconciliation-health", title: "Reconciliation Health", desc: "Matched% per card", icon: Activity },
  { key: "spend-by-category", title: "Spend by Category", desc: "Where the money goes", icon: Tags },
  { key: "spend-by-worker", title: "Spend by Worker", desc: "Per-worker totals", icon: Users },
  { key: "spend-by-department", title: "Spend by Department", desc: "Cost-center breakdown", icon: FolderKanban },
  { key: "spend-by-card", title: "Spend by Card", desc: "Per-card totals", icon: CreditCard },
  { key: "chase-stats", title: "Chase Stats", desc: "Response times & top chased", icon: MessageSquare },
];

const ANY_COMPANY = "__any__";

function downloadCsv(reportKey, params) {
  const tok = localStorage.getItem(TOKEN_KEY);
  const qs = new URLSearchParams({ ...params, format: "csv" }).toString();
  fetch(`${API_BASE}/reports/${reportKey}?${qs}`, { headers: { Authorization: `Bearer ${tok}` } })
    .then((r) => r.blob())
    .then((blob) => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `${reportKey}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    });
}

export function ReportsHome() {
  const navigate = useNavigate();
  return (
    <div data-testid="reports-home">
      <h1 className="mb-1 text-2xl font-semibold text-slate-900 sm:text-3xl">Reports</h1>
      <p className="mb-6 text-sm text-slate-500">Operational stats and CSV exports for accounting + audits.</p>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <Card data-testid="card-digest" className="cursor-pointer transition hover:border-[#0F1E3D]/40"
          onClick={() => navigate("/reports/digest")}>
          <CardHeader><CardTitle className="flex items-center gap-2 text-base"><TrendingUp className="h-4 w-4 text-[#0F1E3D]" /> Health Digest</CardTitle></CardHeader>
          <CardContent className="text-xs text-slate-500">Weekly/monthly snapshot — matched %, top unmatched, response times.</CardContent>
        </Card>
        <Card data-testid="card-driver-chargebacks" className="cursor-pointer transition hover:border-[#0F1E3D]/40"
          onClick={() => navigate("/reports/driver-chargebacks")}>
          <CardHeader><CardTitle className="flex items-center gap-2 text-base"><Truck className="h-4 w-4 text-[#0F1E3D]" /> Driver Chargebacks</CardTitle></CardHeader>
          <CardContent className="text-xs text-slate-500">Per-driver totals for expenses flagged as charge-driver.</CardContent>
        </Card>
        <Card data-testid="card-spend-by-company" className="cursor-pointer transition hover:border-[#0F1E3D]/40"
          onClick={() => navigate("/reports/spend-by-company")}>
          <CardHeader><CardTitle className="flex items-center gap-2 text-base"><Building2 className="h-4 w-4 text-[#0F1E3D]" /> Spend by Company</CardTitle></CardHeader>
          <CardContent className="text-xs text-slate-500">Per-company totals — matched, pending and counts.</CardContent>
        </Card>
        {REPORTS.map((r) => {
          const Icon = r.icon;
          return (
            <Card key={r.key} data-testid={`card-${r.key}`} className="cursor-pointer transition hover:border-[#0F1E3D]/40"
              onClick={() => navigate(`/reports/${r.key}`)}>
              <CardHeader><CardTitle className="flex items-center gap-2 text-base"><Icon className="h-4 w-4 text-[#0F1E3D]" /> {r.title}</CardTitle></CardHeader>
              <CardContent className="text-xs text-slate-500">{r.desc}</CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}

export function ReportDetail() {
  const { key } = useParams();
  const navigate = useNavigate();
  const meta = REPORTS.find((r) => r.key === key);
  const today = new Date().toISOString().slice(0, 10);
  const monthAgo = new Date(Date.now() - 30 * 86400000).toISOString().slice(0, 10);
  const [dateFrom, setDateFrom] = useState(monthAgo);
  const [dateTo, setDateTo] = useState(today);
  const [companyId, setCompanyId] = useState(ANY_COMPANY);
  const [companies, setCompanies] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/companies/active").then((r) => setCompanies(r.data)).catch(() => {});
  }, []);

  const params = useMemo(() => {
    const p = { date_from: `${dateFrom}T00:00:00`, date_to: `${dateTo}T23:59:59` };
    if (companyId !== ANY_COMPANY) p.company_id = companyId;
    return p;
  }, [dateFrom, dateTo, companyId]);

  useEffect(() => {
    setLoading(true); setError("");
    api.get(`/reports/${key}`, { params })
      .then((r) => setData(r.data))
      .catch((e) => setError(extractError(e)))
      .finally(() => setLoading(false));
  }, [key, params]);

  if (!meta) return <Alert variant="destructive"><AlertDescription>Unknown report</AlertDescription></Alert>;

  const isReconHealth = key === "reconciliation-health";
  const isChase = key === "chase-stats";
  const isSpend = key && key.startsWith("spend-by-");
  const maxValue = isSpend && data?.items?.length ? Math.max(...data.items.map((i) => i.total_cents)) : 0;

  return (
    <div data-testid={`report-${key}`}>
      <Button variant="ghost" size="sm" onClick={() => navigate("/reports")} className="mb-3">← All reports</Button>
      <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">{meta.title}</h1>
          <p className="text-sm text-slate-500">{meta.desc}</p>
        </div>
        <div className="flex flex-wrap items-end gap-2">
          {!isChase && (
            <>
              <div><Label className="text-xs">From</Label><Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="date-from" className="w-40" /></div>
              <div><Label className="text-xs">To</Label><Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="date-to" className="w-40" /></div>
              <div>
                <Label className="text-xs">Company</Label>
                <Select value={companyId} onValueChange={setCompanyId}>
                  <SelectTrigger data-testid="report-filter-company" className="w-48"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ANY_COMPANY}>All companies</SelectItem>
                    {companies.map((c) => (
                      <SelectItem key={c.id} value={c.id} data-testid={`report-filter-company-opt-${c.code}`}>
                        {c.code} — {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </>
          )}
          {!isChase && (
            <Button onClick={() => downloadCsv(key, params)} data-testid="export-csv-btn"
              className="bg-[#0F1E3D] text-white hover:bg-[#0F1E3D]/90">
              <Download className="mr-1 h-4 w-4" /> Export CSV
            </Button>
          )}
        </div>
      </div>

      {error && <Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert>}
      {loading ? <div className="py-10 text-center text-slate-500">Loading…</div> : data && (
        <>
          {isReconHealth && (
            <div className="space-y-4">
              <div className="rounded-lg border border-slate-200 bg-white p-5">
                <div className="text-xs uppercase tracking-wide text-slate-500">Overall matched</div>
                <div className="mt-1 text-3xl font-bold text-emerald-700" data-testid="overall-matched-pct">
                  {data.overall.matched_pct.toFixed(1)}%
                </div>
                <div className="mt-1 text-xs text-slate-500">
                  {data.overall.matched} / {data.overall.total} lines matched · {data.overall.ambiguous} ambig · {data.overall.unmatched} unmatched
                </div>
              </div>
              <div className="rounded-lg border border-slate-200 bg-white">
                <table className="w-full text-sm" data-testid="recon-table">
                  <thead><tr className="border-b text-xs uppercase tracking-wide text-slate-500">
                    <th className="p-3 text-left">Card</th><th className="text-right">Matched %</th>
                    <th className="text-right">Matched</th><th className="text-right">Ambig</th>
                    <th className="text-right">Unmatched</th><th className="text-right">Total</th>
                  </tr></thead>
                  <tbody>
                    {data.by_card.map((c) => (
                      <tr key={c.card_id} className="border-b last:border-0">
                        <td className="p-3">{c.card_label} <span className="font-mono text-xs text-slate-400">{c.card_last4}</span></td>
                        <td className="p-3 text-right font-mono">{c.matched_pct.toFixed(1)}%</td>
                        <td className="p-3 text-right font-mono text-emerald-700">{c.matched}</td>
                        <td className="p-3 text-right font-mono text-amber-700">{c.ambiguous}</td>
                        <td className="p-3 text-right font-mono text-rose-700">{c.unmatched}</td>
                        <td className="p-3 text-right font-mono">{c.total}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {isSpend && (
            <div>
              <div className="mb-4 rounded-lg border border-slate-200 bg-white p-4">
                <div className="text-xs uppercase tracking-wide text-slate-500">Total</div>
                <div className="text-2xl font-bold text-[#0F1E3D]" data-testid="spend-total">{formatMoney(data.total_cents)}</div>
                <div className="text-xs text-slate-500">{data.total_count} expenses</div>
              </div>
              <div className="space-y-2">
                {data.items.map((it) => (
                  <div key={it.user_id || it.category_id || it.department_id || it.card_id || it.name} data-testid={`spend-row-${it.name}`}
                    className="flex items-center gap-3 rounded-md border border-slate-200 bg-white p-3">
                    <div className="w-40 truncate text-sm font-medium">{it.name}{it.alt && <span className="ml-1 font-mono text-xs text-slate-400">{it.alt}</span>}</div>
                    <div className="flex-1">
                      <div className="h-2 overflow-hidden rounded bg-slate-100">
                        <div className="h-full bg-[#0F1E3D]" style={{ width: maxValue ? `${(it.total_cents / maxValue) * 100}%` : "0%" }} />
                      </div>
                    </div>
                    <div className="w-28 text-right font-mono text-sm">{formatMoney(it.total_cents)}</div>
                    <div className="w-10 text-right text-xs text-slate-500">{it.count}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {isChase && (
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <Card><CardHeader><CardTitle className="text-base">Chase volume</CardTitle></CardHeader>
                <CardContent className="grid grid-cols-3 gap-2 text-center">
                  <div><div className="text-2xl font-bold text-rose-700" data-testid="chase-open">{data.open}</div><div className="text-xs text-slate-500">open</div></div>
                  <div><div className="text-2xl font-bold text-emerald-700">{data.answered}</div><div className="text-xs text-slate-500">answered</div></div>
                  <div><div className="text-2xl font-bold text-slate-500">{data.cancelled}</div><div className="text-xs text-slate-500">cancelled</div></div>
                </CardContent>
              </Card>
              <Card><CardHeader><CardTitle className="text-base">Avg response time</CardTitle></CardHeader>
                <CardContent><div className="text-2xl font-bold text-[#0F1E3D]" data-testid="avg-response">{data.avg_response_hours.toFixed(1)} hrs</div></CardContent>
              </Card>
              <Card className="md:col-span-2"><CardHeader><CardTitle className="text-base">Top chased workers</CardTitle></CardHeader>
                <CardContent>
                  <table className="w-full text-sm">
                    <thead><tr className="text-xs uppercase tracking-wide text-slate-500"><th className="text-left">Worker</th><th className="text-right">Open</th><th className="text-right">Answered</th></tr></thead>
                    <tbody>{data.top_chased_workers.map((w) => (
                      <tr key={w.user_id} className="border-t"><td className="py-2">{w.user_name}</td><td className="text-right font-mono">{w.open}</td><td className="text-right font-mono">{w.answered}</td></tr>
                    ))}</tbody>
                  </table>
                </CardContent>
              </Card>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export function DigestPage() {
  const navigate = useNavigate();
  const [period, setPeriod] = useState("week");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    api.get("/reports/digest", { params: { period } })
      .then((r) => setData(r.data)).finally(() => setLoading(false));
  }, [period]);

  return (
    <div data-testid="digest-page">
      <Button variant="ghost" size="sm" onClick={() => navigate("/reports")} className="mb-3">← All reports</Button>
      <div className="mb-4 flex items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Reconciliation Health Digest</h1>
          <p className="text-sm text-slate-500">In-app snapshot for accounting teams.</p>
        </div>
        <div className="flex gap-2">
          <Button variant={period === "week" ? "default" : "outline"} onClick={() => setPeriod("week")} data-testid="period-week"
            className={period === "week" ? "bg-[#0F1E3D] text-white hover:bg-[#0F1E3D]/90" : ""}>Week</Button>
          <Button variant={period === "month" ? "default" : "outline"} onClick={() => setPeriod("month")} data-testid="period-month"
            className={period === "month" ? "bg-[#0F1E3D] text-white hover:bg-[#0F1E3D]/90" : ""}>Month</Button>
        </div>
      </div>
      {loading ? <div className="py-10 text-center text-slate-500">Loading…</div> : data && (
        <div className="space-y-4">
          <Card>
            <CardHeader><CardTitle className="flex items-center gap-2 text-base"><TrendingUp className="h-4 w-4" /> Headline</CardTitle></CardHeader>
            <CardContent className="flex items-center gap-6">
              <div><div className="text-4xl font-bold text-emerald-700" data-testid="digest-matched-pct">{data.headline_matched_pct.toFixed(1)}%</div><div className="text-xs text-slate-500">lines matched</div></div>
              <div><div className="text-2xl font-bold text-[#0F1E3D]">{data.chase_response_avg_hours.toFixed(1)}h</div><div className="text-xs text-slate-500">avg chase response</div></div>
              <div><div className="text-2xl font-bold text-rose-700">{data.chase_open}</div><div className="text-xs text-slate-500">open chases</div></div>
              <div><div className="text-2xl font-bold text-amber-700">{data.expenses_missing_comment_over_7d}</div><div className="text-xs text-slate-500">expenses missing comment &gt;7d</div></div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader><CardTitle className="text-base">Top unmatched vendors</CardTitle></CardHeader>
            <CardContent>{data.top_unmatched_vendors.length === 0 ? <p className="text-sm text-slate-500">None — clean week.</p> : (
              <ul className="space-y-1 text-sm">{data.top_unmatched_vendors.map((v) => (
                <li key={v.token} className="flex justify-between" data-testid={`digest-vendor-${v.token}`}><span className="font-mono">{v.token}</span><Badge variant="secondary">{v.count}</Badge></li>
              ))}</ul>
            )}</CardContent>
          </Card>
          <Card>
            <CardHeader><CardTitle className="text-base">By card</CardTitle></CardHeader>
            <CardContent>
              <table className="w-full text-sm">
                <thead><tr className="text-xs uppercase tracking-wide text-slate-500"><th className="text-left">Card</th><th className="text-right">Matched %</th><th className="text-right">Lines</th></tr></thead>
                <tbody>{data.by_card.map((c) => (
                  <tr key={c.card_id} className="border-t"><td className="py-2">{c.card_label} <span className="font-mono text-xs text-slate-400">{c.card_last4}</span></td><td className="text-right font-mono">{c.matched_pct.toFixed(1)}%</td><td className="text-right font-mono">{c.total}</td></tr>
                ))}</tbody>
              </table>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}


export function DriverChargebacksPage() {
  const navigate = useNavigate();
  const today = new Date().toISOString().slice(0, 10);
  const monthAgo = new Date(Date.now() - 30 * 86400000).toISOString().slice(0, 10);
  const [dateFrom, setDateFrom] = useState(monthAgo);
  const [dateTo, setDateTo] = useState(today);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const params = useMemo(() => ({
    date_from: `${dateFrom}T00:00:00`,
    date_to: `${dateTo}T23:59:59`,
  }), [dateFrom, dateTo]);

  useEffect(() => {
    setLoading(true); setError("");
    api.get("/reports/driver-chargebacks", { params })
      .then((r) => setData(r.data))
      .catch((e) => setError(extractError(e)))
      .finally(() => setLoading(false));
  }, [params]);

  const maxValue = data?.items?.length ? Math.max(...data.items.map((i) => i.total_cents)) : 0;

  return (
    <div data-testid="report-driver-chargebacks">
      <Button variant="ghost" size="sm" onClick={() => navigate("/reports")} className="mb-3">← All reports</Button>
      <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Driver Chargebacks</h1>
          <p className="text-sm text-slate-500">Per-driver totals for expenses flagged charge-driver.</p>
        </div>
        <div className="flex items-end gap-2">
          <div><Label className="text-xs">From</Label><Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="dc-date-from" className="w-40" /></div>
          <div><Label className="text-xs">To</Label><Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="dc-date-to" className="w-40" /></div>
          <Button onClick={() => downloadCsv("driver-chargebacks", params)} data-testid="dc-export-csv-btn"
            className="bg-[#0F1E3D] text-white hover:bg-[#0F1E3D]/90">
            <Download className="mr-1 h-4 w-4" /> Export CSV
          </Button>
        </div>
      </div>

      {error && <Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert>}
      {loading ? <div className="py-10 text-center text-slate-500">Loading…</div> : data && (
        <>
          {data.note && (
            <Alert className="mb-4 border-sky-200 bg-sky-50 text-sky-900" data-testid="dc-note-banner">
              <AlertDescription className="text-xs">{data.note}</AlertDescription>
            </Alert>
          )}
          <div className="mb-4 rounded-lg border border-slate-200 bg-white p-4">
            <div className="text-xs uppercase tracking-wide text-slate-500">Total charged back</div>
            <div className="text-2xl font-bold text-[#0F1E3D]" data-testid="dc-total">{formatMoney(data.total_cents)}</div>
            <div className="text-xs text-slate-500">{data.total_count} expense{data.total_count === 1 ? "" : "s"} · {data.items.length} driver{data.items.length === 1 ? "" : "s"}</div>
          </div>

          {data.items.length === 0 ? (
            <div className="rounded-md border border-dashed border-slate-300 px-6 py-12 text-center" data-testid="dc-empty">
              <p className="text-sm font-medium text-slate-700">No driver chargebacks in this window</p>
              <p className="text-xs text-slate-500">Flag an expense as charge-driver to see it here.</p>
            </div>
          ) : (
            <>
              <div className="mb-6 space-y-2">
                {data.items.map((b) => (
                  <div key={b.driver_id} data-testid={`dc-bar-${b.driver_id}`}
                    className="flex items-center gap-3 rounded-md border border-slate-200 bg-white p-3">
                    <div className="w-48 truncate text-sm font-medium">{b.driver_label}</div>
                    <div className="flex-1">
                      <div className="h-2 overflow-hidden rounded bg-slate-100">
                        <div className="h-full bg-[#0F1E3D]" style={{ width: maxValue ? `${(b.total_cents / maxValue) * 100}%` : "0%" }} />
                      </div>
                    </div>
                    <div className="w-28 text-right font-mono text-sm">{formatMoney(b.total_cents)}</div>
                    <div className="w-10 text-right text-xs text-slate-500">{b.count}</div>
                  </div>
                ))}
              </div>

              <div className="rounded-lg border border-slate-200 bg-white">
                <table className="w-full text-sm" data-testid="dc-table">
                  <thead className="border-b text-xs uppercase tracking-wide text-slate-500">
                    <tr>
                      <th className="p-3 text-left">Driver</th>
                      <th className="p-3 text-right">Total</th>
                      <th className="p-3 text-right">Count</th>
                      <th className="p-3 text-left">Top categories</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.items.map((b) => (
                      <tr key={b.driver_id} className="border-b last:border-0" data-testid={`dc-row-${b.driver_id}`}>
                        <td className="p-3">{b.driver_label} <span className="font-mono text-xs text-slate-400">{b.driver_id}</span></td>
                        <td className="p-3 text-right font-mono">{formatMoney(b.total_cents)}</td>
                        <td className="p-3 text-right font-mono">{b.count}</td>
                        <td className="p-3 text-xs text-slate-600">
                          {b.top_categories.length === 0 ? "—" : b.top_categories.map((c) => (
                            <span key={`${b.driver_id}-${c.category_name}`} className="mr-2 inline-flex items-center gap-1">
                              <Badge variant="secondary" className="bg-slate-100">{c.category_name}</Badge>
                              <span className="font-mono text-slate-500">{formatMoney(c.total_cents)}</span>
                            </span>
                          ))}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}

export function SpendByCompanyPage() {
  const navigate = useNavigate();
  const today = new Date().toISOString().slice(0, 10);
  const monthAgo = new Date(Date.now() - 30 * 86400000).toISOString().slice(0, 10);
  const [dateFrom, setDateFrom] = useState(monthAgo);
  const [dateTo, setDateTo] = useState(today);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const params = useMemo(() => ({
    date_from: `${dateFrom}T00:00:00`,
    date_to: `${dateTo}T23:59:59`,
  }), [dateFrom, dateTo]);

  useEffect(() => {
    setLoading(true); setError("");
    api.get("/reports/spend-by-company", { params })
      .then((r) => setData(r.data))
      .catch((e) => setError(extractError(e)))
      .finally(() => setLoading(false));
  }, [params]);

  const maxValue = data?.items?.length ? Math.max(...data.items.map((i) => i.total_cents)) : 0;

  return (
    <div data-testid="report-spend-by-company">
      <Button variant="ghost" size="sm" onClick={() => navigate("/reports")} className="mb-3">← All reports</Button>
      <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Spend by Company</h1>
          <p className="text-sm text-slate-500">Per-company totals — matched, pending and counts.</p>
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <div><Label className="text-xs">From</Label><Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="sbc-date-from" className="w-40" /></div>
          <div><Label className="text-xs">To</Label><Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="sbc-date-to" className="w-40" /></div>
          <Button onClick={() => downloadCsv("spend-by-company", params)} data-testid="sbc-export-csv-btn"
            className="bg-[#0F1E3D] text-white hover:bg-[#0F1E3D]/90">
            <Download className="mr-1 h-4 w-4" /> Export CSV
          </Button>
        </div>
      </div>

      {error && <Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert>}
      {loading ? <div className="py-10 text-center text-slate-500">Loading…</div> : data && (
        <>
          <div className="mb-4 rounded-lg border border-slate-200 bg-white p-4">
            <div className="text-xs uppercase tracking-wide text-slate-500">Total</div>
            <div className="text-2xl font-bold text-[#0F1E3D]" data-testid="sbc-total">{formatMoney(data.total_cents)}</div>
            <div className="text-xs text-slate-500">{data.total_count} expense{data.total_count === 1 ? "" : "s"} · {data.items.length} compan{data.items.length === 1 ? "y" : "ies"}</div>
          </div>

          {data.items.length === 0 ? (
            <div className="rounded-md border border-dashed border-slate-300 px-6 py-12 text-center" data-testid="sbc-empty">
              <p className="text-sm font-medium text-slate-700">No expenses with company in this window</p>
              <p className="text-xs text-slate-500">Tag an expense with a company to see it here.</p>
            </div>
          ) : (
            <>
              <div className="mb-6 space-y-2">
                {data.items.map((b) => (
                  <div key={b.company_id} data-testid={`sbc-bar-${b.company_code}`}
                    className="flex items-center gap-3 rounded-md border border-slate-200 bg-white p-3">
                    <div className="w-48 truncate text-sm">
                      <span className="font-medium">{b.company_name}</span>
                      <span className="ml-1 font-mono text-xs text-slate-400">{b.company_code}</span>
                    </div>
                    <div className="flex-1">
                      <div className="h-2 overflow-hidden rounded bg-slate-100">
                        <div className="h-full bg-[#0F1E3D]" style={{ width: maxValue ? `${(b.total_cents / maxValue) * 100}%` : "0%" }} />
                      </div>
                    </div>
                    <div className="w-28 text-right font-mono text-sm">{formatMoney(b.total_cents)}</div>
                    <div className="w-10 text-right text-xs text-slate-500">{b.count}</div>
                  </div>
                ))}
              </div>

              <div className="rounded-lg border border-slate-200 bg-white">
                <table className="w-full text-sm" data-testid="sbc-table">
                  <thead className="border-b text-xs uppercase tracking-wide text-slate-500">
                    <tr>
                      <th className="p-3 text-left">Company</th>
                      <th className="p-3 text-right">Total</th>
                      <th className="p-3 text-right">Matched</th>
                      <th className="p-3 text-right">Pending</th>
                      <th className="p-3 text-right">Count</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.items.map((b) => (
                      <tr key={b.company_id} className="border-b last:border-0" data-testid={`sbc-row-${b.company_code}`}>
                        <td className="p-3">
                          <span className="font-medium">{b.company_name}</span>
                          <span className="ml-1 font-mono text-xs text-slate-400">{b.company_code}</span>
                        </td>
                        <td className="p-3 text-right font-mono">{formatMoney(b.total_cents)}</td>
                        <td className="p-3 text-right font-mono text-emerald-700">{formatMoney(b.matched_cents)}</td>
                        <td className="p-3 text-right font-mono text-amber-700">{formatMoney(b.pending_cents)}</td>
                        <td className="p-3 text-right font-mono">{b.count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
